import { 
  OtpRequest,
  OtpVerificationRequest, 
  AuthResponse,
  Chat,
  Message,
  User,
  UserDocument,
  InventoryScheduleDocument,
  BookingsDocument,
  ApiError,
  InventoryDocument,
  InventoryAdminListItem,
  InventoryDetailsResponse,
  InventorySlotResponse,
  NameUpdateRequestVo,
  InventoryUpdateVo,
  InventoryScheduleRequest,
  UpdateInventorySlotRequest,
  AddImageRequestVo,
  InventoryInfoRequestVo,
  InventoryInfoDocument,
  SlotFilterData,
  CoordinatorData,
  ExpenseData,
  EarningData,
  AssignCoordinatorRequest,
  AddExpenseRequest,
  BookingBasicInfo,
  Destination,
  DestinationPageResponse,
  DestinationCreateRequest,
  DestinationUpdateRequest
} from '../types';
import { FilterList } from '../constants/filterData';

class ApiService {
  private baseURL: string;

  constructor() {
    this.baseURL = 'https://sales.bozapp.online';
    // this.baseURL = 'http://localhost:8080';
  }

  private getAuthHeaders(): Record<string, string> {
    const userId = localStorage.getItem('user_id');
    // 'user_uid' now holds the boz-auth RS256 access JWT (was the legacy opaque token).
    const token = localStorage.getItem('user_uid');

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (userId && token) {
      headers['Authorization'] = `Bearer ${token}`;
      headers['x-ca-user-id'] = userId;
      // Add super admin header for coordinator assignment (from env)
      const superAdmin = import.meta.env.VITE_BOZ_SUPER_ADMIN;
      if (superAdmin) {
        headers['x-boz-super-admin'] = superAdmin;
      }
    }

    return headers;
  }

  private refreshPromise: Promise<string | null> | null = null;

  // Single-flight refresh: concurrent 401s share one refresh call.
  private async refreshAccessToken(): Promise<string | null> {
    if (!this.refreshPromise) {
      this.refreshPromise = (async (): Promise<string | null> => {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) return null;
        try {
          const res = await fetch(`${this.baseURL}/v1/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
          });
          if (!res.ok) return null;
          const data = await res.json();
          if (!data?.token) return null;
          // 'user_uid' holds the access JWT; persist the rotated refresh token.
          localStorage.setItem('user_uid', data.token);
          if (data.refreshToken) localStorage.setItem('refresh_token', data.refreshToken);
          return data.token as string;
        } catch {
          return null;
        }
      })().finally(() => { this.refreshPromise = null; });
    }
    return this.refreshPromise;
  }

  // fetch + refresh-on-401 + retry once. On refresh failure, clears the session.
  private async fetchWithRefresh(input: string, init: RequestInit = {}): Promise<Response> {
    const response = await fetch(input, init);
    if (response.status !== 401 || input.includes('/v1/auth/')) {
      return response;
    }
    const newToken = await this.refreshAccessToken();
    if (!newToken) {
      // Refresh failed (no/expired refresh token) — end the session so the app routes
      // back to login instead of getting stuck "authenticated" with every call 401ing.
      this.clearUserData();
      try {
        const { useAuthStore } = await import('../stores/authStore');
        useAuthStore.setState({
          user: null,
          userId: null,
          userUid: null,
          userRole: null,
          isAuthenticated: false,
        });
      } catch {
        // store reset is best-effort; localStorage is already cleared above
      }
      return response;
    }
    // retry once with fresh auth headers merged over the original request headers
    const retryInit: RequestInit = {
      ...init,
      headers: { ...(init.headers as Record<string, string> | undefined), ...this.getAuthHeaders() },
    };
    return await fetch(input, retryInit);
  }

  private async handleResponse(response: Response): Promise<any> {
    if (!response.ok) {
      let errorMessage = 'An error occurred';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        // If response is not JSON, use status text
        errorMessage = response.statusText || errorMessage;
      }
      
      const error: ApiError = {
        message: errorMessage,
        code: `HTTP_${response.status}`,
      };
      throw error;
    }
    
    // Handle empty responses
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }
    
    return response.text();
  }

  // User data management
  private storeUserData(authResponse: AuthResponse): void {
    localStorage.setItem('user_id', authResponse.userId.toString());
    // 'user_uid' now holds the boz-auth access JWT (sent as Authorization: Bearer).
    localStorage.setItem('user_uid', authResponse.token);
    if (authResponse.refreshToken) {
      localStorage.setItem('refresh_token', authResponse.refreshToken);
    }
    localStorage.setItem('user_role', authResponse.role);
  }

  private clearUserData(): void {
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_uid');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_role');
  }

  // Authentication API calls
  async requestOtp(phoneNumber: string): Promise<string> {
    const response = await fetch(`${this.baseURL}/v1/user/request/otp/${phoneNumber}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    return await this.handleResponse(response);
  }

  async verifyOtp(phoneNumber: string, otp: string): Promise<AuthResponse> {
    // boz-auth login (RS256). Replaces the removed GET /v1/user/validate/phone/{}/otp/{}.
    const response = await fetch(`${this.baseURL}/v1/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ phoneNumber, otp: Number(otp), source: 'web' }),
    });

    const authResponse = await this.handleResponse(response);
    this.storeUserData(authResponse);
    return authResponse;
  }

  async logout(): Promise<void> {
    this.clearUserData();
  }

  // Chat API calls
  async getChats(searchQuery?: string, type?: string, updatedOn?: number, tripIds?: string[]): Promise<Chat[]> {
    const params = new URLSearchParams();
    if (searchQuery) params.append('searchQuery', searchQuery);
    if (type) params.append('type', type);
    if (updatedOn) params.append('updatedOn', updatedOn.toString());
    if (tripIds && tripIds.length > 0) {
      params.append('tripIds', tripIds.join(','));
    }
    
    const queryString = params.toString();
    const url = `${this.baseURL}/v1/lead/chats${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.fetchWithRefresh(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  async getMessages(userId: number): Promise<Message[]> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/lead/chat/${userId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  async sendMessage(userId: number, messageData: {
    message: string;
    type?: string;
    action?: string;
    inventoryId?: string;
  }): Promise<any> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/lead/send/message/${userId}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(messageData),
    });
    
    return await this.handleResponse(response);
  }

  // Image upload API calls
  async uploadMedia(file: File, onProgress?: (progress: number) => void): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mimeType', file.type);

    const userId = localStorage.getItem('user_id');
    // 'user_uid' now holds the boz-auth access JWT (sent as Authorization: Bearer).
    const token = localStorage.getItem('user_uid');

    const headers: Record<string, string> = {};

    if (userId && token) {
      headers['Authorization'] = `Bearer ${token}`;
      headers['x-ca-user-id'] = userId;
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = (event.loaded / event.total) * 100;
            onProgress(progress);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (error) {
            reject(new Error('Invalid response format'));
          }
        } else {
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            reject(new Error(errorResponse.Error || 'Upload failed'));
          } catch {
            reject(new Error(`Upload failed: ${xhr.statusText}`));
          }
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('timeout', () => {
        reject(new Error('Upload timeout'));
      });

      xhr.open('POST', `${this.baseURL}/v1/lead/upload/media`);
      
      // Set headers
      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      xhr.timeout = 30000; // 30 second timeout
      xhr.send(formData);
    });
  }

  // ImageCloudinary upload API
  async uploadToImageCloudinary(file: File, onProgress?: (progress: number) => void): Promise<{
    id: string;
    original_url: string;
    transform_url_template: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = (event.loaded / event.total) * 100;
            onProgress(progress);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (error) {
            reject(new Error('Invalid response format'));
          }
        } else {
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            reject(new Error(errorResponse.error || errorResponse.message || 'Upload failed'));
          } catch {
            reject(new Error(`Upload failed: ${xhr.statusText}`));
          }
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('timeout', () => {
        reject(new Error('Upload timeout'));
      });

      xhr.open('POST', 'https://img.bozapp.in/api/v1/upload');
      
      // Set API key header (from env)
      const imageApiKey = import.meta.env.VITE_IMAGE_API_KEY;
      if (imageApiKey) {
        xhr.setRequestHeader('x-api-key', imageApiKey);
      }

      xhr.timeout = 60000; // 60 second timeout
      xhr.send(formData);
    });
  }

  async sendImageMessage(userId: number, imageUrl: string): Promise<any> {
    const messageData = {
      message: imageUrl,
      type: 'Image',
      action: null,
      inventoryId: null
    };

    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/lead/send/message/${userId}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(messageData),
    });
    
    return await this.handleResponse(response);
  }

  async sendFastReplyMessage(userId: number, messageData: {
    message: string;
    type: 'QuickReply' | 'Text';
    action: string;
    inventoryId: string;
  }): Promise<any> {
    console.log('API Service: sendFastReplyMessage called');
    console.log('API Service: userId:', userId);
    console.log('API Service: messageData:', messageData);
    
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/lead/send/message/${userId}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(messageData),
    });
    
    console.log('API Service: response status:', response.status);
    console.log('API Service: response ok:', response.ok);
    
    const result = await this.handleResponse(response);
    console.log('API Service: result:', result);
    return result;
  }

  // Chat status management API calls
  async markChatAsRead(userId: number): Promise<any> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/lead/unread/${userId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  async markChatAsPending(userId: number, status: boolean): Promise<any> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/lead/pending/${userId}?status=${status}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  // User profile API calls
  async getUserInfo(): Promise<UserDocument> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/user/info/self`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  // Admin slots API calls
  async getSlots(params?: {
    startTime?: number;
    endTime?: number;
    oldBookings?: boolean;
    assignment?: boolean;
    all?: boolean;
    inventoryId?: string;
    coordinators?: boolean;
    hideCancelled?: boolean;
  }): Promise<InventoryScheduleDocument[]> {
    const queryParams = new URLSearchParams();
    
    if (params?.startTime) queryParams.append('startTime', params.startTime.toString());
    if (params?.endTime) queryParams.append('endTime', params.endTime.toString());
    if (params?.oldBookings !== undefined) queryParams.append('old', params.oldBookings.toString());
    if (params?.assignment !== undefined) queryParams.append('assignment', params.assignment.toString());
    if (params?.all !== undefined) queryParams.append('all', params.all.toString());
    if (params?.inventoryId) queryParams.append('inventory_id', params.inventoryId);
    if (params?.coordinators !== undefined) queryParams.append('coordinators', params.coordinators.toString());
    if (params?.hideCancelled !== undefined) queryParams.append('hideCancelled', params.hideCancelled.toString());
    
    const queryString = queryParams.toString();
    const url = `${this.baseURL}/v1/admin/slots${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.fetchWithRefresh(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  // Admin bookings API calls
  async getAllBookings(params?: {
    query?: string;
    status?: string;
  }): Promise<BookingsDocument[]> {
    const queryParams = new URLSearchParams();
    
    if (params?.query) queryParams.append('query', params.query);
    if (params?.status) queryParams.append('status', params.status);
    
    const queryString = queryParams.toString();
    const url = `${this.baseURL}/v1/admin/bookings${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.fetchWithRefresh(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  // Get bookings CSV data
  async getBookingsCsv(month?: string): Promise<BookingBasicInfo[]> {
    const queryParams = new URLSearchParams();
    if (month) {
      queryParams.append('month', month);
    }
    
    const queryString = queryParams.toString();
    const url = `${this.baseURL}/v1/admin/bookings/csv${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.fetchWithRefresh(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  // Create booking API call
  async addBooking(bookingData: {
    slotId: string;
    inventoryId: string;
    participants: Array<{
      name: string;
      phoneNumber: string;
      email: string;
      gender: 'male' | 'female';
      pickupLocation: {
        id: string;
        name: string;
        displayText: string;
      } | null;
    }>;
    ticketPrice: number;
    finalPrice: number;
    amountPaid: number;
    discount: number;
    source: string;
    notes: string;
    sendSms: boolean;
    sendEmail: boolean;
  }): Promise<BookingsDocument> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v2/payment/add/booking`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        slotId: bookingData.slotId,
        couponId: null,
        partialPaymentRequested: false,
        list: bookingData.participants.map((p, index) => ({
          name: p.name,
          phoneNumber: p.phoneNumber,
          email: p.email,
          gender: p.gender === 'male' ? 1 : 2, // Convert to numeric
          tripLocation: p.pickupLocation,
          primaryUser: index === 0, // First traveller is primary user
          slotId: bookingData.slotId
        })),
        partialPaymentAlertShown: false,
        ticketPrice: Math.round(bookingData.ticketPrice),
        totalAmount: Math.round(bookingData.finalPrice),
        paidAmount: Math.round(bookingData.amountPaid),
        discountAmount: Math.round(bookingData.discount),
        source: bookingData.source,
        note: bookingData.notes,
        sendSms: bookingData.sendSms,
        sendEmail: bookingData.sendEmail
      }),
    });
    
    return await this.handleResponse(response);
  }

  // Inventory Management API calls
  async createInventory(request: NameUpdateRequestVo): Promise<InventoryDocument> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/create`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });
    
    return await this.handleResponse(response);
  }

  async duplicateInventory(inventoryId: string, request: NameUpdateRequestVo): Promise<InventoryDocument> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/duplicate/${inventoryId}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });
    
    return await this.handleResponse(response);
  }

  async updateInventory(inventoryId: string, request: InventoryUpdateVo): Promise<InventoryDocument> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/update/${inventoryId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });
    
    return await this.handleResponse(response);
  }

  async getInventoryAllItems(params?: {
    hideDeactivated?: boolean;
    status?: number;
    variant?: string;
  }): Promise<InventoryAdminListItem[]> {
    const queryParams = new URLSearchParams();
    
    if (params?.hideDeactivated !== undefined) {
      queryParams.append('hide_deactivated', params.hideDeactivated.toString());
    }
    
    if (params?.status !== undefined) {
      queryParams.append('status', params.status.toString());
    }
    
    if (params?.variant) {
      queryParams.append('variant', params.variant);
    }
    
    const queryString = queryParams.toString();
    const url = `${this.baseURL}/v1/inventory/all_items${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.fetchWithRefresh(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  async getInventoryDetails(inventoryId: string): Promise<InventoryDetailsResponse> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/${inventoryId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  async getInventoryDetailsForUpdate(inventoryId: string): Promise<InventoryDetailsResponse> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/update/${inventoryId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({}),
    });
    
    return await this.handleResponse(response);
  }

  async getSlotsList(inventoryId: string): Promise<InventoryScheduleDocument[]> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/${inventoryId}/slots`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  async activateInventory(inventoryId: string): Promise<any> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/activate/${inventoryId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  async deactivateInventory(inventoryId: string): Promise<any> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/deactivate/${inventoryId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  async publishInventory(inventoryId: string): Promise<any> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/publish/${inventoryId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  async scheduleInventory(inventoryId: string, request: InventoryScheduleRequest): Promise<InventoryScheduleDocument> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/${inventoryId}/schedule`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });
    
    return await this.handleResponse(response);
  }

  async getInventorySlot(slotId: string): Promise<InventorySlotResponse> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/slot/${slotId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  async addBookings(slotId: string): Promise<InventoryScheduleDocument> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/slot/${slotId}/booking/add`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  async removeBookings(slotId: string): Promise<InventoryScheduleDocument> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/slot/${slotId}/booking/remove`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  async deleteSlot(slotId: string): Promise<InventoryScheduleDocument> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/slot/${slotId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  async updateInventorySlot(slotId: string, request: UpdateInventorySlotRequest): Promise<InventoryScheduleDocument> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/slot/${slotId}/update`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });
    
    return await this.handleResponse(response);
  }

  async updateSlotStatus(slotId: string, status: boolean): Promise<InventoryScheduleDocument> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/slot/${slotId}/update/status?status=${status}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  async updateSlotPrice(slotId: string, price: number): Promise<InventoryScheduleDocument> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/slot/${slotId}/update/price?price=${price}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  async addImage(inventoryId: string, request: AddImageRequestVo): Promise<InventoryDocument> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/add_image/${inventoryId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });
    
    return await this.handleResponse(response);
  }

  async addInventoryInfo(inventoryId: string, request: InventoryInfoRequestVo): Promise<InventoryInfoDocument> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/${inventoryId}/info/add`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });
    
    return await this.handleResponse(response);
  }

  async updateInventoryInfo(infoId: string, request: InventoryInfoRequestVo): Promise<InventoryInfoDocument> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/info/${infoId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });
    
    return await this.handleResponse(response);
  }

  async deleteInventoryInfo(infoId: string): Promise<string> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/info/${infoId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  async getInventoryInfoList(inventoryId: string): Promise<InventoryInfoDocument[]> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/${inventoryId}/info`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  async updatePinnedStatus(infoId: string, pinned: boolean): Promise<InventoryInfoDocument> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/info/${infoId}/pin?pinned=${pinned}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  async updateCoordinatorStatus(infoId: string, coordinator: boolean): Promise<InventoryInfoDocument> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/info/${infoId}/coordinator?coordinator=${coordinator}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
    });
    
    return await this.handleResponse(response);
  }

  // Assignment-related API methods
  
  // Get slot filter data (date ranges) - using local data like admin-web
  async getSlotFilterData(): Promise<SlotFilterData[]> {
    // Return local filter data instead of making API call
    // This matches admin-web implementation
    return Promise.resolve(FilterList as SlotFilterData[]);
  }

  // Get coordinators in date range
  async getCoordinatorsInRange(params: {
    startTime: number;
    endTime: number;
    match: boolean;
    slotId?: string;
  }): Promise<CoordinatorData[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('startTime', params.startTime.toString());
    queryParams.append('endTime', params.endTime.toString());
    queryParams.append('match', params.match.toString());
    if (params.slotId) {
      queryParams.append('slotId', params.slotId);
    }

    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/coordinator/availability?${queryParams}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    return this.handleResponse(response);
  }

  // Assign coordinator to slot
  async assignCoordinatorToSlot(request: AssignCoordinatorRequest): Promise<void> {
    const queryParams = new URLSearchParams();
    queryParams.append('level', request.level.toString());
    
    const url = `${this.baseURL}/v1/coordinator/assign/slot/${request.slotId}/user/${request.coordinatorId}?${queryParams}`;
    
    const response = await this.fetchWithRefresh(url, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({}),
    });
    
    return this.handleResponse(response);
  }

  // Remove coordinator from slot
  async removeCoordinatorFromSlot(slotId: string, coordinatorId: string, coordinatorAssociationId: string): Promise<void> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/admin/coordinators/remove`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        slotId,
        coordinatorId,
        coordinatorAssociationId
      }),
    });
    
    return this.handleResponse(response);
  }

  // Confirm all coordinators for a slot
  async confirmAllSlotCoordinators(slotId: string): Promise<void> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/admin/coordinators/confirm-all`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ slotId }),
    });
    
    return this.handleResponse(response);
  }

  // Get slot expenses
  async getSlotExpenses(slotId: string): Promise<ExpenseData[]> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/admin/slots/${slotId}/expenses`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    return this.handleResponse(response);
  }

  // Get slot earnings
  async getSlotEarnings(slotId: string): Promise<EarningData> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/admin/slots/${slotId}/earnings`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    return this.handleResponse(response);
  }

  // Add expense to slot
  async addSlotExpense(request: AddExpenseRequest): Promise<ExpenseData> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/admin/expenses`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });
    
    return this.handleResponse(response);
  }

  // Delete expense
  async deleteExpense(expenseId: string): Promise<void> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/admin/expenses/${expenseId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    
    return this.handleResponse(response);
  }

  // Update slot field
  async updateSlotField(slotId: string, fieldName: string, fieldValue: string): Promise<InventoryScheduleDocument> {
    const updateData: any = {};
    updateData[fieldName] = fieldValue;
    
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/slot/${slotId}/update`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(updateData),
    });
    
    return this.handleResponse(response);
  }

  // Get inventory data for coordinator amounts
  async getInventoryData(inventoryId: string): Promise<any> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/inventory/${inventoryId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    return this.handleResponse(response);
  }

  // ==========================================
  // Destination Management APIs
  // ==========================================

  /**
   * Get all destinations with pagination and optional search
   * GET https://sales.bozapp.online/v1/destinations?page=0&size=10&q=search
   * Headers: x-ca-user-id: {user_id}, x-ca-user-uid: {user_uid}
   * Note: API may return either a paginated Page object or a plain array
   */
  async getDestinations(page: number = 0, size: number = 10, searchQuery?: string): Promise<DestinationPageResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });
    
    if (searchQuery && searchQuery.trim()) {
      params.append('q', searchQuery.trim());
    }

    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/destinations?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    const data = await this.handleResponse(response);
    
    // Handle both response formats: array or paginated object
    if (Array.isArray(data)) {
      // API returned a plain array - convert to paginated format
      return {
        content: data,
        totalElements: data.length,
        totalPages: 1,
        size: data.length,
        number: 0,
        first: true,
        last: true,
      };
    }
    
    // API returned a paginated object
    return data;
  }

  /**
   * Get destination by slug
   * GET https://sales.bozapp.online/v1/destinations/{slug}
   * Headers: x-ca-user-id: {user_id}, x-ca-user-uid: {user_uid}
   */
  async getDestinationBySlug(slug: string): Promise<Destination> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/destinations/${encodeURIComponent(slug)}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return await this.handleResponse(response);
  }

  /**
   * Get destination by ID
   * GET https://sales.bozapp.online/v1/destinations/{id}
   * Note: This uses the same endpoint as slug, but we'll use ID for internal operations
   */
  async getDestinationById(id: string): Promise<Destination> {
    // First try to get by slug, if that fails, we might need to search
    // For now, we'll use the slug endpoint - in production you might need a different endpoint
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/destinations/${encodeURIComponent(id)}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return await this.handleResponse(response);
  }

  /**
   * Create a new destination
   * POST https://sales.bozapp.online/v1/destinations
   * Headers: x-ca-user-id: {user_id}, x-ca-user-uid: {user_uid}
   * Body: DestinationCreateRequest
   */
  async createDestination(data: DestinationCreateRequest): Promise<Destination> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/destinations`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    return await this.handleResponse(response);
  }

  /**
   * Update an existing destination
   * PUT https://sales.bozapp.online/v1/destinations/{id}
   * Headers: x-ca-user-id: {user_id}, x-ca-user-uid: {user_uid}
   * Body: DestinationUpdateRequest
   */
  async updateDestination(id: string, data: DestinationUpdateRequest): Promise<Destination> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/destinations/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    return await this.handleResponse(response);
  }

  /**
   * Delete a destination
   * DELETE https://sales.bozapp.online/v1/destinations/{id}
   * Headers: x-ca-user-id: {user_id}, x-ca-user-uid: {user_uid}
   */
  async deleteDestination(id: string): Promise<void> {
    const response = await this.fetchWithRefresh(`${this.baseURL}/v1/destinations/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      // DELETE returns 204 No Content on success, so any other status is an error
      try {
        await this.handleResponse(response);
      } catch (error) {
        throw error;
      }
    }
    // 204 No Content - success, no response body
  }
}

// API Documentation
/*
REQUEST OTP API:
GET https://sales.bozapp.online/v1/user/request/otp/{phone_number}
Path Variable: { phone_number: "+919xxxxxxxxx" }
Response: "Success" (200 OK) or null/error

VALIDATE OTP API:
GET https://sales.bozapp.online/v1/user/validate/phone/{phone_number}/otp/{otp}
Path Variables: { phone_number: "+919xxxxxxxxx", otp: "123456" }
Response (Success 200): {
  "id": "UID",
  "phoneNumber": "+919xxxxxxxxx",
  "userId": 12345,
  "createdOn": 1761414538241,
  "expiredOn": 1761414638241,
  "role": "sales",
  "user": {
    "id": 12345,
    "phoneNumber": "+919xxxxxxxxx",
    "name": "Ashraf"
  }
}

GET CHATS API:
GET https://sales.bozapp.online/v1/lead/chats
Headers: x-ca-user-id: {user_id}, x-ca-user-uid: {user_uid}
Query Params: searchQuery, type, updatedOn
Response: [
  {
    "userId": 73750,
    "phoneNumber": "+919449414907",
    "name": "Coronus Gennison",
    "tripName": "Chikmagalur",
    "lastMessage": "Looking for which date?",
    "lastMessageTime": 1761414538241,
    "lastMessageBy": "BOT",
    "leadId": "KIaeSqN9KH",
    "qualified": false,
    "disableBot": false,
    "unreadCount": 0
  }
]

GET MESSAGES API:
GET https://sales.bozapp.online/v1/lead/chat/{userId}
Headers: x-ca-user-id: {user_id}, x-ca-user-uid: {user_uid}
Response: [
  {
    "id": "jinO4uzgHiTDHlxZQycj4H6V3otHoCmJ8VgI",
    "createdByName": "Bhuvan",
    "byBoz": false,
    "type": "message_received",
    "receivedData": {
      "message": {
        "message": "Hello! Can I get more info on Coorg trip?"
      }
    }
  }
]
*/

export const apiService = new ApiService();
export default apiService;
